import type { CompletionContext, CompletionResult, CompletionSource } from "@codemirror/autocomplete";
import { hoverTooltip, type EditorView, type Tooltip } from "@codemirror/view";
import type { BbelFunctionInfo, ScopeDescriptor, ScopeNode } from "@/types/bbel-types";

export interface BbelCompletion {
  label: string;
  type: "variable" | "function";
  /** Groups the bare-level list into labelled sections; absent for member completions. */
  section?: "Scopes" | "Functions" | "Search";
  /** Function signature, shown dimmed beside the label (functions only). */
  detail?: string;
  /** Function summary, shown in the side info panel (functions only). */
  info?: string;
  /** Secret member — listed for discoverability but disabled: greyed, lock icon, accept is a no-op. */
  locked?: boolean;
  /**
   * Bracket-form insert (`[0]`, `["content-type"]`) — applied in place of the member dot the user
   * typed (`items.` + accept `[0]` → `items[0]`), since bracket access replaces dot access rather
   * than following it.
   */
  insertBracket?: string;
}

const IDENTIFIER = /^[A-Za-z_$][\w$]*$/;

/** Finds the `]` closing the bracket at `open`, skipping quoted content. -1 = unclosed. */
function closingBracket(chain: string, open: number): number {
  let quote: string | null = null;
  for (let i = open + 1; i < chain.length; i++) {
    const c = chain[i];
    if (quote) {
      if (c === "\\") i++;
      else if (c === quote) quote = null;
    } else if (c === '"' || c === "'") quote = c;
    else if (c === "]") return i;
  }
  return -1;
}

/**
 * Parses the member chain before the cursor — dots, numeric hops (`[0]`), and quoted keys
 * (`["content-type"]`) — into resolved segments plus the trailing partial being typed.
 * Segments match ScopeNode.segment: hops stay literal (`[0]`), quoted keys unquote to the raw key.
 * Returns null when the chain isn't completable: mid-bracket, a computed key (`[i]`), or ending at
 * a `]` with no dot yet.
 */
export function parseChain(chain: string): { segments: string[]; partial: string } | null {
  const segments: string[] = [];
  let i = 0;
  let end: "dot" | "ident" | "bracket" = "dot"; // empty chain completes as a bare partial
  while (i < chain.length) {
    const ch = chain[i];
    if (ch === ".") {
      end = "dot";
      i++;
      continue;
    }
    if (ch === "[") {
      const close = closingBracket(chain, i);
      if (close < 0) return null;
      const inner = chain.slice(i + 1, close);
      if (/^\d+$/.test(inner)) segments.push(`[${inner}]`);
      else if (/^"(?:[^"\\]|\\.)*"$/.test(inner)) segments.push(inner.slice(1, -1).replace(/\\(.)/g, "$1"));
      else if (/^'(?:[^'\\]|\\.)*'$/.test(inner)) segments.push(inner.slice(1, -1).replace(/\\(.)/g, "$1"));
      else return null;
      end = "bracket";
      i = close + 1;
      continue;
    }
    const m = /^[\w$]+/.exec(chain.slice(i));
    if (!m) return null;
    segments.push(m[0]);
    end = "ident";
    i += m[0].length;
  }

  if (end === "ident") return { segments: segments.slice(0, -1), partial: segments[segments.length - 1] };
  if (end === "dot") return { segments, partial: "" };
  return null; // ends at `]` — nothing to complete until the next dot
}

/**
 * Node paths for every resolved level of the chain at the caret (partial dropped) — the picker
 * expands these so typing and browsing stay in sync. Join rules mirror how node paths are built
 * (bbel-scope-output.memberPath): dot for identifiers, verbatim for hops, bracket-quoted otherwise.
 */
export function chainAncestorPaths(chain: string): string[] {
  // A chain ending at `]` isn't completable, but it IS a resolved level — a trailing dot makes it parse.
  const parsed = parseChain(chain) ?? parseChain(`${chain}.`);
  if (!parsed) return [];

  const paths: string[] = [];
  let path = "";
  for (const seg of parsed.segments) {
    if (path === "") path = seg;
    else if (seg.startsWith("[")) path += seg;
    else if (IDENTIFIER.test(seg)) path += `.${seg}`;
    else path += `["${seg.replace(/"/g, '\\"')}"]`;
    paths.push(path);
  }
  return paths;
}

// Section order in the popup: scopes first, functions below, deep-search hits last.
const SECTIONS: Record<string, { name: string; rank: number }> = {
  Scopes: { name: "Scopes", rank: 1 },
  Functions: { name: "Functions", rank: 2 },
  Search: { name: "Search", rank: 3 },
};

// Deep search opens at 3 typed chars (shorter partials match half the tree) and caps the section
// so the popup stays readable.
const SEARCH_MIN_CHARS = 3;
const SEARCH_MAX_RESULTS = 15;

/** In-order gapped char match ("repstat" reaches "GetReport…statusCode"); both args pre-lowered. */
function subsequence(needle: string, haystack: string): boolean {
  let i = 0;
  for (const c of haystack) if (c === needle[i]) i++;
  return i === needle.length;
}

/**
 * Global search over the scope tree for a bare partial — the autocomplete counterpart of the
 * palette's search (filterScopeTree): typing "statusCode" surfaces
 * `services.GetReport.output.json.statusCode` without browsing there. Options are labelled with the
 * full path (which is also what accepting inserts). Ranked segment-prefix > segment-substring >
 * path-substring > path-subsequence, shallower first; the rank decides which hits survive the cap —
 * display order within the section is CodeMirror's own match scoring.
 */
function searchMatches(roots: ScopeNode[], partial: string): BbelCompletion[] {
  const q = partial.toLowerCase();
  const hits: { node: ScopeNode; rank: number; depth: number }[] = [];
  const walk = (nodes: ScopeNode[], depth: number) => {
    for (const n of nodes) {
      // Roots themselves are already listed under "Scopes".
      if (depth > 0) {
        const seg = n.segment.toLowerCase();
        const path = n.path.toLowerCase();
        const rank = seg.startsWith(q) ? 0
          : seg.includes(q) ? 1
          : path.includes(q) ? 2
          : subsequence(q, path) ? 3
          : -1;
        if (rank >= 0) hits.push({ node: n, rank, depth });
      }
      if (n.children) walk(n.children, depth + 1);
    }
  };
  walk(roots, 0);
  hits.sort((a, b) => a.rank - b.rank || a.depth - b.depth || a.node.path.localeCompare(b.node.path));
  return hits.slice(0, SEARCH_MAX_RESULTS).map(({ node }) => ({
    label: node.path,
    type: "variable" as const,
    section: "Search" as const,
    ...(node.hint ? { detail: node.hint } : {}),
    ...(node.locked ? { locked: true } : {}),
  }));
}

/**
 * Pure completion logic (#5683). Given the identifier chain immediately before the cursor —
 * "services.Get", "conn", or "services." — return the matching options and the length of the partial
 * being completed (so the caller can compute the replace `from`).
 */
export function bbelCompletions(
  descriptor: ScopeDescriptor,
  chain: string,
): { options: BbelCompletion[]; partialLength: number } {
  const parsed = parseChain(chain);
  if (!parsed) return { options: [], partialLength: 0 };
  const { segments, partial } = parsed;

  // No dot yet: a bare word completes to scope roots + function names, grouped into "Scopes" and
  // "Functions" sections (scopes first) so the list reads clearly.
  if (segments.length === 0) {
    const roots = descriptor.roots
      .filter((r) => r.segment.startsWith(partial))
      .map((r) => ({ label: r.segment, type: "variable" as const, section: "Scopes" as const }));
    const functions = descriptor.functions
      .filter((f) => f.name.startsWith(partial))
      .map((f) => ({
        label: f.name,
        type: "function" as const,
        section: "Functions" as const,
        detail: f.signature,
        info: f.summary,
      }));
    const search = partial.length >= SEARCH_MIN_CHARS ? searchMatches(descriptor.roots, partial) : [];
    return { options: [...roots, ...functions, ...search], partialLength: partial.length };
  }

  // Navigate the tree by the path segments; suggest the resolved node's children.
  let level: ScopeNode[] | undefined = descriptor.roots;
  for (const seg of segments) {
    const node: ScopeNode | undefined = level?.find((n) => n.segment === seg);
    level = node?.children;
    if (!level) return { options: [], partialLength: partial.length };
  }
  const options: BbelCompletion[] = level
    .filter((n) => n.segment.startsWith(partial))
    // Where the display label differs from the inserted segment (services: "1. GetReport" vs the
    // id the runtime resolves), show it dimmed beside the option. Locked members (secret fields)
    // stay listed for discoverability but render disabled and don't insert.
    .map((n) => ({
      label: n.segment,
      type: "variable" as const,
      ...(n.label !== n.segment ? { detail: n.label } : {}),
      ...(n.locked ? { locked: true } : {}),
      ...(bracketInsert(n.segment) ? { insertBracket: bracketInsert(n.segment)! } : {}),
    }));
  return { options, partialLength: partial.length };
}

/** Bracket-form insert for segments dot-access can't reach: hops verbatim, non-identifiers quoted. */
function bracketInsert(segment: string): string | undefined {
  if (segment.startsWith("[")) return segment;
  if (!IDENTIFIER.test(segment)) return `["${segment.replace(/"/g, '\\"')}"]`;
  return undefined;
}

/** Inside a `{{ … }}` slot when the nearest preceding delimiter before the cursor is an opener. */
export function insideSlot(before: string): boolean {
  const open = before.lastIndexOf("{{");
  return open >= 0 && open > before.lastIndexOf("}}");
}

/**
 * CodeMirror completion source over a (reactive) scope descriptor — active only inside a `{{ … }}`
 * slot. The descriptor is read through a getter so it always reflects the current workflow.
 */
export function bbelCompletionSource(getDescriptor: () => ScopeDescriptor): CompletionSource {
  return (context: CompletionContext): CompletionResult | null => {
    const before = context.state.sliceDoc(0, context.pos);
    if (!insideSlot(before)) return null;

    const token = context.matchBefore(/[\w$.[\]"']*/);
    if (!token) return null;
    // Don't auto-open on an empty token; wait for a typed character or explicit invoke (Ctrl-Space).
    if (token.from === token.to && !context.explicit) return null;

    const { options, partialLength } = bbelCompletions(getDescriptor(), token.text);
    if (options.length === 0) return null;

    return {
      from: context.pos - partialLength,
      options: options.map((o) => ({
        label: o.label,
        type: o.type,
        ...(o.detail ? { detail: o.detail } : {}),
        ...(o.info ? { info: o.info } : {}),
        ...(o.section ? { section: SECTIONS[o.section] } : {}),
        // Locked (secret) members render disabled — the `locked` flag reaches optionClass in
        // BbelField, and accepting one is a no-op (CM has no native disabled entries).
        ...(o.locked ? { locked: true, apply: () => {} } : {}),
        // Bracket segments replace the member dot instead of following it: accepting `[0]` after
        // `items.` yields `items[0]`, never the `items.[0]` the grammar rejects.
        ...(o.insertBracket
          ? {
              apply: (view: EditorView, _c: unknown, from: number, to: number) => {
                const start = view.state.sliceDoc(Math.max(0, from - 1), from) === "." ? from - 1 : from;
                view.dispatch({
                  changes: { from: start, to, insert: o.insertBracket! },
                  selection: { anchor: start + o.insertBracket!.length },
                });
              },
            }
          : {}),
      })),
      // No validFor: the search section is ranked-and-capped, so the option set must be recomputed
      // as the partial grows — a frozen cap could pin a stale top-15. The source is pure and cheap.
    };
  };
}

/**
 * CodeMirror hover tooltip over function tokens — hovering a word inside a `{{ … }}` slot that names a
 * registered function shows its signature and summary (the "how do I complete jsonDecode" hint). The
 * function set is read through a getter so it tracks the current descriptor.
 */
export function bbelFunctionHover(getFunctions: () => BbelFunctionInfo[]) {
  return hoverTooltip((view, pos): Tooltip | null => {
    const word = view.state.wordAt(pos);
    if (!word) return null;
    if (!insideSlot(view.state.sliceDoc(0, word.from))) return null;

    const name = view.state.sliceDoc(word.from, word.to);
    const fn = getFunctions().find((f) => f.name === name);
    if (!fn) return null;

    return {
      pos: word.from,
      end: word.to,
      above: true,
      create() {
        const dom = document.createElement("div");
        dom.className = "bbel-fn-hover";
        const sig = document.createElement("code");
        sig.className = "bbel-fn-hover-sig";
        sig.textContent = fn.signature;
        const sum = document.createElement("div");
        sum.className = "bbel-fn-hover-sum";
        sum.textContent = fn.summary;
        dom.append(sig, sum);
        return { dom };
      },
    };
  });
}
